import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  useTheme,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Debt, Income, Expense } from "../../lib/types";
import * as expenseUtils from "../../lib/expenseUtils";
import { getTodayLocal } from "../../lib/dateUtils";
import debtDB from "../../services/database/debtDB";
import incomeDB from "../../services/database/incomeDB";
import { expenseAPI, pageShareAPI } from "../../services/api/api";
import { plaidCreditCardService } from "../../services/plaid/plaidCreditCardService";
import PageShareModal from "../common/PageShareModal";
import { useShareAction } from "../../contexts/ShareActionContext";

const HOURLY_RATES = [22, 27, 30, 45, 75, 150, 250];
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_DAYS_PER_WEEK = 5;
const FORECASTING_PROFILES_STORAGE_KEY = "debt_forecasting_profiles";
const CARD_TITLE_FONT_SIZE = "14px";
const CARD_VALUE_FONT_SIZE = "18px";

type ForecastingProfileSettings = {
  daysPerWeek: number;
  hoursPerDay: number;
  deductionMode: "percent" | "amount";
  deductionPercent: number;
  deductionAmountAnnual: number;
  excludedDebtIds: string[];
  includeAutoLoans: boolean;
  includeMortgageLoans: boolean;
  includeRvLoans: boolean;
  includeBusinessLoans: boolean;
};

type ForecastingProfile = {
  id: string;
  name: string;
  settings: ForecastingProfileSettings;
  createdAt: number;
};

type SharedForecastingPayload = {
  version: 1;
  profileName: string;
  settings: ForecastingProfileSettings;
};

const base64UrlDecode = (input: string): string => {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (padded.length % 4)) % 4;
  const normalized = padded + "=".repeat(padLength);
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
};

const base64UrlToBuffer = (input: string): ArrayBuffer => {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (padded.length % 4)) % 4;
  const normalized = padded + "=".repeat(padLength);
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const normalizeSharedPayload = (
  payload: any
): SharedForecastingPayload | null => {
  if (!payload) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload) as SharedForecastingPayload;
    } catch (error) {
      console.error("[Forecasting] Failed to parse shared payload", error);
      return null;
    }
  }
  return payload as SharedForecastingPayload;
};

const deriveEncryptionKey = async (
  password: string,
  salt: ArrayBuffer
): Promise<CryptoKey> => {
  const baseKey = await window.crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 120000,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

const decryptSharedPayload = async (
  encoded: string,
  password: string
): Promise<SharedForecastingPayload> => {
  const decoded = base64UrlDecode(encoded);
  const parsed = JSON.parse(decoded) as {
    salt: string;
    iv: string;
    data: string;
  };
  const key = await deriveEncryptionKey(
    password,
    base64UrlToBuffer(parsed.salt)
  );
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(base64UrlToBuffer(parsed.iv)) },
    key,
    base64UrlToBuffer(parsed.data)
  );
  const payload = JSON.parse(
    new TextDecoder().decode(decrypted)
  ) as SharedForecastingPayload;
  return payload;
};

const loadForecastingProfiles = (): ForecastingProfile[] => {
  try {
    const stored = localStorage.getItem(FORECASTING_PROFILES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveForecastingProfiles = (profiles: ForecastingProfile[]): void => {
  localStorage.setItem(
    FORECASTING_PROFILES_STORAGE_KEY,
    JSON.stringify(profiles)
  );
};

const generateProfileId = (): string => {
  return `forecast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getTodayYmd = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

type TaxBracket = {
  rate: number;
  lowerBound: number;
  upperBound: number;
};

const FEDERAL_TAX_BRACKETS_2025_SINGLE: TaxBracket[] = [
  { rate: 0.1, lowerBound: 0, upperBound: 11925 },
  { rate: 0.12, lowerBound: 11925, upperBound: 48475 },
  { rate: 0.22, lowerBound: 48475, upperBound: 103350 },
  { rate: 0.24, lowerBound: 103350, upperBound: 197300 },
  { rate: 0.32, lowerBound: 197300, upperBound: 250525 },
  { rate: 0.35, lowerBound: 250525, upperBound: 626350 },
  { rate: 0.37, lowerBound: 626350, upperBound: Infinity },
];

const OREGON_TAX_BRACKETS_2025_SINGLE: TaxBracket[] = [
  { rate: 0.0475, lowerBound: 0, upperBound: 4399 },
  { rate: 0.0675, lowerBound: 4399, upperBound: 11049 },
  { rate: 0.0875, lowerBound: 11049, upperBound: 124999 },
  { rate: 0.099, lowerBound: 124999, upperBound: Infinity },
];

const STANDARD_DEDUCTION_2025_SINGLE = 15000;
const MEDICARE_TAX_RATE = 0.0145;
const SOCIAL_SECURITY_TAX_RATE = 0.062;
const SS_WAGE_BASE_2025 = 176100;

const calculateProgressiveTax = (
  income: number,
  brackets: TaxBracket[]
): number => {
  if (income <= 0) return 0;

  let totalTax = 0;

  for (let i = 0; i < brackets.length; i += 1) {
    const bracket = brackets[i];
    const prevUpperBound = i > 0 ? brackets[i - 1].upperBound : 0;

    if (income > prevUpperBound) {
      const taxableInBracket =
        Math.min(income, bracket.upperBound) - prevUpperBound;
      totalTax += taxableInBracket * bracket.rate;

      if (income <= bracket.upperBound) {
        break;
      }
    }
  }

  return totalTax;
};

const calculateYearlyTaxes = (
  grossIncome: number,
  deductions: number
): number => {
  const taxableIncome = Math.max(
    0,
    grossIncome - STANDARD_DEDUCTION_2025_SINGLE - deductions
  );

  const federalIncomeTax = calculateProgressiveTax(
    taxableIncome,
    FEDERAL_TAX_BRACKETS_2025_SINGLE
  );
  const stateIncomeTax = calculateProgressiveTax(
    taxableIncome,
    OREGON_TAX_BRACKETS_2025_SINGLE
  );
  const medicareTax = grossIncome * MEDICARE_TAX_RATE;
  const socialSecurityTax =
    Math.min(grossIncome, SS_WAGE_BASE_2025) * SOCIAL_SECURITY_TAX_RATE;

  return federalIncomeTax + stateIncomeTax + medicareTax + socialSecurityTax;
};

const Forecasting: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { setShareHandler } = useShareAction();

  const [debts, setDebts] = useState<Debt[]>([]);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [daysPerWeek, setDaysPerWeek] = useState(DEFAULT_DAYS_PER_WEEK);
  const [hoursPerDay, setHoursPerDay] = useState(DEFAULT_HOURS_PER_DAY);
  const [deductionMode, setDeductionMode] = useState<"percent" | "amount">(
    "percent"
  );
  const [deductionPercent, setDeductionPercent] = useState(25);
  const [deductionAmountAnnual, setDeductionAmountAnnual] = useState(0);
  const [debtPickerOpen, setDebtPickerOpen] = useState(false);
  const [addRateDialogOpen, setAddRateDialogOpen] = useState(false);
  const [newHourlyRateInput, setNewHourlyRateInput] = useState("");
  const [customHourlyRates, setCustomHourlyRates] = useState<number[]>([]);
  const [addDebtDialogOpen, setAddDebtDialogOpen] = useState(false);
  const [isAddingDebt, setIsAddingDebt] = useState(false);
  const [newDebtForm, setNewDebtForm] = useState<Omit<Debt, "id">>({
    account: "",
    creditor: "",
    invoiceLink: "",
    type: "Other",
    balance: 0,
    payoffStrategy: "Standard",
    creditAffecting: "Yes",
    creditLimit: 0,
    paydownAmount: 0,
    maxMonthlyPayment: 0,
    dueDate: getTodayYmd(),
    status: "Active",
    statementCredits: 0,
    dateBorrowed: "",
    linkedExpenseId: undefined,
    linkedAccountId: undefined,
    debtQuality: undefined,
    canDefault: false,
    isShared: false,
  });
  const [excludedDebtIds, setExcludedDebtIds] = useState<string[]>([]);
  const [includeAutoLoans, setIncludeAutoLoans] = useState(false);
  const [includeMortgageLoans, setIncludeMortgageLoans] = useState(false);
  const [includeRvLoans, setIncludeRvLoans] = useState(false);
  const [includeBusinessLoans, setIncludeBusinessLoans] = useState(false);
  const [profiles, setProfiles] = useState<ForecastingProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [breakdownModalType, setBreakdownModalType] = useState<
    "monthlyPayment" | "monthlyIncome" | "monthlyExpenses" | null
  >(null);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [pendingSharedPayload, setPendingSharedPayload] = useState<
    string | null
  >(null);
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load debts (including Plaid credit cards)
        const [manualDebts, plaidCreditCards, allIncomes, allExpenses] =
          await Promise.all([
            debtDB.getAllDebts(),
            plaidCreditCardService.getCreditCardAccounts(),
            incomeDB.getAllIncome(),
            expenseAPI.getAll(),
          ]);

        setDebts([...manualDebts, ...plaidCreditCards]);
        setIncomes(allIncomes);
        setExpenses(allExpenses);
      } catch (error) {
        console.error("Failed to load forecasting data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const loadedProfiles = loadForecastingProfiles();
    setProfiles(loadedProfiles);
  }, []);

  const [searchParams] = useSearchParams();
  const sharedParam = searchParams.get("shared");
  const shareIdParam = searchParams.get("shareId");

  useEffect(() => {
    if (!sharedParam) return;
    const isProtected = searchParams.get("protected") === "1";
    if (isProtected) {
      setPendingSharedPayload(sharedParam);
      setPendingShareId(null);
      setUnlockDialogOpen(true);
      console.debug("[Forecasting] Shared profile requires password");
      return;
    }
    try {
      const decoded = base64UrlDecode(sharedParam);
      const payload = JSON.parse(decoded) as SharedForecastingPayload;
      applyProfileSettings(payload.settings);
      setSelectedProfileId("");
      console.debug("[Forecasting] Loaded shared profile", {
        profileName: payload.profileName,
      });
    } catch (error) {
      console.error("[Forecasting] Failed to load shared profile", error);
    }
  }, [sharedParam, searchParams]);

  useEffect(() => {
    if (!shareIdParam) return;
    let active = true;
    const loadShare = async () => {
      try {
        console.debug("[Forecasting] Loading shareId", {
          shareId: shareIdParam,
        });
        const info = await pageShareAPI.getInfo(shareIdParam);
        if (!active) return;
        if (info.pageType !== "forecasting") {
          console.error("[Forecasting] Share is not forecasting", info);
          return;
        }
        if (info.requiresPassword) {
          setPendingShareId(shareIdParam);
          setPendingSharedPayload(null);
          setUnlockDialogOpen(true);
          console.debug("[Forecasting] Share requires password");
          return;
        }
        const result = await pageShareAPI.validate(shareIdParam);
        if (!active) return;
        const payload = normalizeSharedPayload(result.payload);
        if (payload) {
          applyProfileSettings(payload.settings);
          setSelectedProfileId("");
          console.debug("[Forecasting] Loaded shareId profile", {
            profileName: payload.profileName,
          });
        }
      } catch (error) {
        console.error("[Forecasting] Failed to load shareId", error);
      }
    };
    loadShare();
    return () => {
      active = false;
    };
  }, [shareIdParam]);

  // Calculate totals
  const getDebtKey = (debt: Debt, index: number) => debt.id ?? `idx-${index}`;

  const excludedDebtIdSet = useMemo(
    () => new Set(excludedDebtIds),
    [excludedDebtIds]
  );

  const activeDebts = useMemo(() => {
    return debts.filter(
      (debt, index) => !excludedDebtIdSet.has(getDebtKey(debt, index))
    );
  }, [debts, excludedDebtIdSet]);

  const getDebtCategoryMatches = (debt: Debt) => {
    const text =
      `${debt.type ?? ""} ${debt.account ?? ""} ${debt.creditor ?? ""}`
        .toLowerCase()
        .trim();
    return {
      auto: text.includes("auto"),
      mortgage: text.includes("mortgage"),
      rv: /\brv\b/.test(text) || text.includes("rv loan"),
      business: text.includes("business"),
    };
  };

  const includedDebts = useMemo(() => {
    return activeDebts.filter((debt) => {
      const matches = getDebtCategoryMatches(debt);
      if (matches.auto && !includeAutoLoans) return false;
      if (matches.mortgage && !includeMortgageLoans) return false;
      if (matches.rv && !includeRvLoans) return false;
      if (matches.business && !includeBusinessLoans) return false;
      return true;
    });
  }, [
    activeDebts,
    includeAutoLoans,
    includeMortgageLoans,
    includeRvLoans,
    includeBusinessLoans,
  ]);

  const monthlyExpenseMeta = useMemo(() => {
    const baseExpenses = expenses.filter(
      (expense) => !expense.isRecurringInstance
    );
    const currentMonthExpenses =
      expenseUtils.getCurrentMonthExpenses(baseExpenses);
    const skippedCount = currentMonthExpenses.filter(
      (expense) => expense.isSkipped
    ).length;
    const monthlyExpenseItems = currentMonthExpenses
      .filter((expense) => !expense.isSkipped)
      .map((expense, index) => ({
        id: expense.id || `expense_${index}`,
        label: expense.name || "Expense",
        amount: expenseUtils.getEffectiveExpenseAmount(expense, activeDebts),
      }));
    const totalMonthlyExpenses = monthlyExpenseItems.reduce(
      (total, item) => total + item.amount,
      0
    );

    return {
      totalMonthlyExpenses,
      currentMonthCount: currentMonthExpenses.length,
      skippedCount,
      baseExpenseCount: baseExpenses.length,
      totalExpenseCount: expenses.length,
      monthlyExpenseItems,
    };
  }, [expenses, activeDebts]);

  const nextMonthIncomes = useMemo(() => {
    const referenceDate = getTodayLocal();
    const nextMonthStart = new Date(
      Date.UTC(
        referenceDate.getUTCFullYear(),
        referenceDate.getUTCMonth() + 1,
        1
      )
    );
    const monthAfterNextStart = new Date(
      Date.UTC(
        referenceDate.getUTCFullYear(),
        referenceDate.getUTCMonth() + 2,
        1
      )
    );

    const normalizeDate = (value: unknown): Date | null => {
      if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
      if (typeof value === "string") {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const isInNextMonth = (value: unknown): boolean => {
      const date = normalizeDate(value);
      if (!date) return false;
      return date >= nextMonthStart && date < monthAfterNextStart;
    };

    return incomes.filter(
      (income) =>
        isInNextMonth(income.receivedDate) ||
        isInNextMonth(income.expectedByDate)
    );
  }, [incomes]);

  const totals = useMemo(() => {
    const totalDebtBalance = includedDebts.reduce(
      (sum, d) => sum + Math.abs(Number(d.balance || 0)),
      0
    );
    const totalDebtPaydown = includedDebts.reduce(
      (sum, d) => sum + Math.abs(Number((d.paydownAmount ?? d.balance) || 0)),
      0
    );
    const totalMonthlyDebtPayment = includedDebts.reduce(
      (sum, d) => sum + Number(d.maxMonthlyPayment || 0),
      0
    );

    const totalMonthlyIncome = nextMonthIncomes.reduce(
      (sum, i) => sum + Number(i.invoiceTotal || 0),
      0
    );

    return {
      totalDebtBalance,
      totalDebtPaydown,
      totalMonthlyDebtPayment,
      totalMonthlyIncome,
      totalMonthlyExpenses: monthlyExpenseMeta.totalMonthlyExpenses,
      netMonthly: totalMonthlyIncome - monthlyExpenseMeta.totalMonthlyExpenses,
    };
  }, [includedDebts, nextMonthIncomes, monthlyExpenseMeta]);

  const annualExpenses = useMemo(
    () => totals.totalMonthlyExpenses * 12,
    [totals.totalMonthlyExpenses]
  );
  const hourlyRates = useMemo(() => {
    const merged = [...HOURLY_RATES, ...customHourlyRates].filter(
      (rate) => Number.isFinite(rate) && rate > 0
    );
    return Array.from(new Set(merged)).sort((a, b) => a - b);
  }, [customHourlyRates]);
  const hasSelectedProfile = Boolean(selectedProfileId);
  const annualWorkDays = useMemo(() => daysPerWeek * 52, [daysPerWeek]);
  const monthlyWorkDays = useMemo(() => annualWorkDays / 12, [annualWorkDays]);

  // Calculate payoff time for each hourly rate
  const payoffCalculations = useMemo(() => {
    const targetAmount = totals.totalDebtBalance;

    return hourlyRates.map((rate) => {
      const dailyEarnings = rate * hoursPerDay;
      const weeklyEarnings = dailyEarnings * daysPerWeek;
      const monthlyEarnings = dailyEarnings * monthlyWorkDays;
      const yearlyEarnings = dailyEarnings * annualWorkDays;
      const deductionAmount =
        deductionMode === "percent"
          ? (yearlyEarnings * deductionPercent) / 100
          : deductionAmountAnnual;
      const deductibleExpenses = annualExpenses;
      const yearlyTaxes = calculateYearlyTaxes(
        yearlyEarnings,
        deductionAmount + deductibleExpenses
      );
      const yearlyNet = yearlyEarnings - yearlyTaxes - annualExpenses;
      const netDaily = annualWorkDays > 0 ? yearlyNet / annualWorkDays : 0;
      const netWeekly = netDaily * daysPerWeek;
      const netMonthly = yearlyNet / 12;

      const daysToPayoff =
        netDaily > 0 ? targetAmount / netDaily : Number.POSITIVE_INFINITY;
      const weeksToPayoff =
        netWeekly > 0 ? targetAmount / netWeekly : Number.POSITIVE_INFINITY;
      const monthsToPayoff =
        netMonthly > 0 ? targetAmount / netMonthly : Number.POSITIVE_INFINITY;
      const yearsToPayoff =
        yearlyNet > 0 ? targetAmount / yearlyNet : Number.POSITIVE_INFINITY;

      return {
        rate,
        dailyEarnings,
        weeklyEarnings,
        monthlyEarnings,
        yearlyEarnings,
        yearlyTaxes,
        yearlyNet,
        deductionAmount,
        deductibleExpenses,
        annualExpenses,
        netDaily,
        netWeekly,
        netMonthly,
        daysToPayoff,
        weeksToPayoff,
        monthsToPayoff,
        yearsToPayoff,
      };
    });
  }, [
    totals.totalDebtBalance,
    daysPerWeek,
    hoursPerDay,
    annualExpenses,
    deductionMode,
    deductionPercent,
    deductionAmountAnnual,
    annualWorkDays,
    monthlyWorkDays,
    hourlyRates,
  ]);

  useEffect(() => {
    if (isLoading) return;
    console.debug("[Forecasting] Tax assumptions", {
      taxYear: 2025,
      filingStatus: "single",
      state: "OR",
      incomeSource: "w2",
      standardDeduction: STANDARD_DEDUCTION_2025_SINGLE,
      daysPerWeek,
      hoursPerDay,
      deductionMode,
      deductionPercent,
      deductionAmountAnnual,
      includeAutoLoans,
      includeMortgageLoans,
      includeRvLoans,
      includeBusinessLoans,
    });
    console.debug("[Forecasting] Payoff projections", {
      totalDebtBalance: totals.totalDebtBalance,
      annualExpenses,
      projections: payoffCalculations.map((calc) => ({
        rate: calc.rate,
        yearlyGross: calc.yearlyEarnings,
        yearlyTaxes: calc.yearlyTaxes,
        deductionAmount: calc.deductionAmount,
        deductibleExpenses: calc.deductibleExpenses,
        annualExpenses: calc.annualExpenses,
        yearlyNet: calc.yearlyNet,
        netDaily: calc.netDaily,
        netWeekly: calc.netWeekly,
        netMonthly: calc.netMonthly,
        daysToPayoff: calc.daysToPayoff,
        weeksToPayoff: calc.weeksToPayoff,
        monthsToPayoff: calc.monthsToPayoff,
        yearsToPayoff: calc.yearsToPayoff,
      })),
    });
  }, [
    isLoading,
    daysPerWeek,
    hoursPerDay,
    totals.totalDebtBalance,
    annualExpenses,
    payoffCalculations,
    deductionMode,
    deductionPercent,
    deductionAmountAnnual,
    includeAutoLoans,
    includeMortgageLoans,
    includeRvLoans,
    includeBusinessLoans,
  ]);

  useEffect(() => {
    if (isLoading) return;
    console.debug("[Forecasting] Monthly expenses inputs", {
      totalExpenseCount: monthlyExpenseMeta.totalExpenseCount,
      baseExpenseCount: monthlyExpenseMeta.baseExpenseCount,
      currentMonthCount: monthlyExpenseMeta.currentMonthCount,
      skippedCount: monthlyExpenseMeta.skippedCount,
      totalMonthlyExpenses: monthlyExpenseMeta.totalMonthlyExpenses,
    });
  }, [isLoading, monthlyExpenseMeta]);

  useEffect(() => {
    if (isLoading) return;
    console.debug("[Forecasting] Debt selection", {
      totalDebts: debts.length,
      includedDebts: includedDebts.length,
      excludedDebts: excludedDebtIds.length,
      excludedDebtIds,
      includeAutoLoans,
      includeMortgageLoans,
      includeRvLoans,
      includeBusinessLoans,
    });
  }, [
    isLoading,
    debts.length,
    includedDebts.length,
    excludedDebtIds,
    includeAutoLoans,
    includeMortgageLoans,
    includeRvLoans,
    includeBusinessLoans,
  ]);

  const handleOpenDebtPicker = () => {
    console.debug("[Forecasting] Open debt selector");
    setDebtPickerOpen(true);
  };

  const handleCloseDebtPicker = () => {
    console.debug("[Forecasting] Close debt selector");
    setDebtPickerOpen(false);
  };

  const resetNewDebtForm = () => {
    setNewDebtForm({
      account: "",
      creditor: "",
      invoiceLink: "",
      type: "Other",
      balance: 0,
      payoffStrategy: "Standard",
      creditAffecting: "Yes",
      creditLimit: 0,
      paydownAmount: 0,
      maxMonthlyPayment: 0,
      dueDate: getTodayYmd(),
      status: "Active",
      statementCredits: 0,
      dateBorrowed: "",
      linkedExpenseId: undefined,
      linkedAccountId: undefined,
      debtQuality: undefined,
      canDefault: false,
      isShared: false,
    });
  };

  const handleAddDebtFromForecast = async () => {
    if (!newDebtForm.account.trim()) return;
    setIsAddingDebt(true);
    try {
      const created = await debtDB.addDebt({
        ...newDebtForm,
        account: newDebtForm.account.trim(),
        creditor: (newDebtForm.creditor || "").trim(),
      });
      setDebts((prev) => [...prev, created]);
      setAddDebtDialogOpen(false);
      resetNewDebtForm();
      console.debug("[Forecasting] Added debt item", { debtId: created.id });
    } catch (error) {
      console.error("[Forecasting] Failed to add debt item", error);
    } finally {
      setIsAddingDebt(false);
    }
  };

  const handleAddHourlyRate = () => {
    const numeric = Number(newHourlyRateInput);
    if (!Number.isFinite(numeric) || numeric <= 0) return;
    const rounded = Math.round(numeric * 100) / 100;
    setCustomHourlyRates((prev) => {
      if (prev.includes(rounded) || HOURLY_RATES.includes(rounded)) return prev;
      return [...prev, rounded];
    });
    setNewHourlyRateInput("");
    setAddRateDialogOpen(false);
  };

  const handleToggleDebt = (debt: Debt, index: number) => {
    const key = getDebtKey(debt, index);
    setExcludedDebtIds((prev) => {
      const next = new Set(prev);
      const wasExcluded = next.has(key);
      if (wasExcluded) {
        next.delete(key);
      } else {
        next.add(key);
      }
      console.debug("[Forecasting] Toggle debt inclusion", {
        key,
        creditor: debt.creditor,
        account: debt.account,
        balance: debt.balance,
        included: wasExcluded,
      });
      return Array.from(next);
    });
  };

  const buildProfileSettings = (): ForecastingProfileSettings => ({
    daysPerWeek,
    hoursPerDay,
    deductionMode,
    deductionPercent,
    deductionAmountAnnual,
    excludedDebtIds,
    includeAutoLoans,
    includeMortgageLoans,
    includeRvLoans,
    includeBusinessLoans,
  });

  const applyProfileSettings = (settings: ForecastingProfileSettings) => {
    setDaysPerWeek(settings.daysPerWeek ?? DEFAULT_DAYS_PER_WEEK);
    setHoursPerDay(settings.hoursPerDay ?? DEFAULT_HOURS_PER_DAY);
    setDeductionMode(settings.deductionMode ?? "percent");
    setDeductionPercent(settings.deductionPercent ?? 25);
    setDeductionAmountAnnual(settings.deductionAmountAnnual ?? 0);
    setExcludedDebtIds(settings.excludedDebtIds ?? []);
    setIncludeAutoLoans(settings.includeAutoLoans ?? false);
    setIncludeMortgageLoans(settings.includeMortgageLoans ?? false);
    setIncludeRvLoans(settings.includeRvLoans ?? false);
    setIncludeBusinessLoans(settings.includeBusinessLoans ?? false);
  };

  const handleSaveProfile = () => {
    if (!newProfileName.trim()) return;
    const newProfile: ForecastingProfile = {
      id: generateProfileId(),
      name: newProfileName.trim(),
      settings: buildProfileSettings(),
      createdAt: Date.now(),
    };
    const updatedProfiles = [...profiles, newProfile];
    setProfiles(updatedProfiles);
    saveForecastingProfiles(updatedProfiles);
    setSelectedProfileId(newProfile.id);
    setNewProfileName("");
    setSaveDialogOpen(false);
    console.debug("[Forecasting] Save profile", {
      profileId: newProfile.id,
      profileName: newProfile.name,
    });
  };

  const handleSaveOrUpdateProfile = () => {
    if (selectedProfileId) {
      const updatedProfiles = profiles.map((profile) =>
        profile.id === selectedProfileId
          ? { ...profile, settings: buildProfileSettings() }
          : profile
      );
      setProfiles(updatedProfiles);
      saveForecastingProfiles(updatedProfiles);
      console.debug("[Forecasting] Update profile", {
        profileId: selectedProfileId,
      });
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleOpenShareDialog = useCallback(() => {
    setShareDialogOpen(true);
    console.debug("[Forecasting] Open share dialog", {
      profileId: selectedProfileId,
    });
  }, [selectedProfileId]);

  useEffect(() => {
    setShareHandler(() => handleOpenShareDialog);
    return () => setShareHandler(null);
  }, [handleOpenShareDialog, setShareHandler]);

  const handleUnlockSharedProfile = async () => {
    if ((!pendingSharedPayload && !pendingShareId) || !unlockPassword.trim()) {
      setUnlockError("Password required.");
      return;
    }
    setUnlockError("");
    try {
      if (pendingSharedPayload) {
        const payload = await decryptSharedPayload(
          pendingSharedPayload,
          unlockPassword.trim()
        );
        applyProfileSettings(payload.settings);
        setSelectedProfileId("");
        setUnlockDialogOpen(false);
        setPendingSharedPayload(null);
        setUnlockPassword("");
        console.debug("[Forecasting] Unlocked shared profile", {
          profileName: payload.profileName,
        });
        return;
      }

      if (pendingShareId) {
        const result = await pageShareAPI.validate(
          pendingShareId,
          unlockPassword.trim()
        );
        const payload = normalizeSharedPayload(result.payload);
        if (!payload) {
          throw new Error("No payload in share");
        }
        applyProfileSettings(payload.settings);
        setSelectedProfileId("");
        setUnlockDialogOpen(false);
        setPendingShareId(null);
        setUnlockPassword("");
        console.debug("[Forecasting] Unlocked shareId profile", {
          profileName: payload.profileName,
        });
      }
    } catch (error) {
      console.error("[Forecasting] Failed to unlock shared profile", error);
      setUnlockError("Invalid password.");
    }
  };

  const handleLoadProfile = (profileId: string) => {
    if (!profileId) {
      setSelectedProfileId("");
      return;
    }
    const profile = profiles.find((p) => p.id === profileId);
    if (profile) {
      applyProfileSettings(profile.settings);
      setSelectedProfileId(profileId);
      console.debug("[Forecasting] Load profile", {
        profileId: profile.id,
        profileName: profile.name,
      });
    }
  };

  const handleDeleteProfile = (profileId: string) => {
    const updatedProfiles = profiles.filter(
      (profile) => profile.id !== profileId
    );
    setProfiles(updatedProfiles);
    saveForecastingProfiles(updatedProfiles);
    if (selectedProfileId === profileId) {
      setSelectedProfileId("");
    }
    console.debug("[Forecasting] Delete profile", { profileId });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const displayCurrencyOrEmpty = (amount: number) =>
    hasSelectedProfile ? formatCurrency(amount) : "—";

  const monthlyPaymentItems = useMemo(
    () =>
      includedDebts.map((debt, index) => ({
        id: getDebtKey(debt, index),
        label:
          [debt.creditor, debt.account].filter(Boolean).join(" • ") || "Debt",
        amount: Number(debt.maxMonthlyPayment || 0),
      })),
    [includedDebts]
  );

  const monthlyIncomeItems = useMemo(
    () =>
      nextMonthIncomes.map((income, index) => ({
        id: income.id || `income_${index}`,
        label: income.client || "Income",
        amount: Number(income.invoiceTotal || 0),
      })),
    [nextMonthIncomes]
  );

  const breakdownItems = useMemo(() => {
    if (breakdownModalType === "monthlyPayment") return monthlyPaymentItems;
    if (breakdownModalType === "monthlyIncome") return monthlyIncomeItems;
    if (breakdownModalType === "monthlyExpenses")
      return monthlyExpenseMeta.monthlyExpenseItems;
    return [];
  }, [
    breakdownModalType,
    monthlyExpenseMeta.monthlyExpenseItems,
    monthlyIncomeItems,
    monthlyPaymentItems,
  ]);

  const breakdownTitle = useMemo(() => {
    if (breakdownModalType === "monthlyPayment")
      return "Monthly Payment Breakdown";
    if (breakdownModalType === "monthlyIncome")
      return "Monthly Income Breakdown";
    if (breakdownModalType === "monthlyExpenses")
      return "Monthly Expenses Breakdown";
    return "";
  }, [breakdownModalType]);

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatNumber = (num: number, decimals: number = 1) => {
    if (num < 1) return `${Math.round(num * 10) / 10}`;
    return num.toFixed(decimals);
  };

  const formatTimeDisplay = (
    days: number,
    weeks: number,
    months: number,
    years: number
  ) => {
    if (
      !Number.isFinite(days) ||
      !Number.isFinite(weeks) ||
      !Number.isFinite(months) ||
      !Number.isFinite(years)
    ) {
      return "—";
    }
    if (years >= 1) {
      const remainingMonths = Math.round((years % 1) * 12);
      if (remainingMonths > 0) {
        return `${Math.floor(years)}y ${remainingMonths}m`;
      }
      return `${formatNumber(years, 1)} years`;
    }
    if (months >= 1) {
      return `${formatNumber(months, 1)} months`;
    }
    if (weeks >= 1) {
      return `${formatNumber(weeks, 1)} weeks`;
    }
    return `${formatNumber(days, 1)} days`;
  };

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Debt Payoff Forecasting
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 4,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton width={100} height={20} />
                <Skeleton width={80} height={32} />
              </CardContent>
            </Card>
          ))}
        </Box>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Debt Payoff Forecasting
      </Typography>

      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            lg: "repeat(6, 1fr)",
          },
          gap: 2,
          mb: 4,
        }}
      >
        {/* Total Debt Balance */}
        <Card
          sx={{
            borderTop: "4px solid #e53e3e",
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
            cursor: "pointer",
          }}
          onClick={handleOpenDebtPicker}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
            >
              Total Debt
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: CARD_VALUE_FONT_SIZE,
                color: "#e53e3e",
              }}
            >
              {hasSelectedProfile
                ? `-${formatCurrency(totals.totalDebtBalance)}`
                : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasSelectedProfile
                ? `Included ${includedDebts.length} of ${debts.length}`
                : "No profile selected"}
            </Typography>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 1,
                backgroundColor: "transparent",
                "&:before": { display: "none" },
                "& .MuiAccordionSummary-root": {
                  minHeight: 28,
                  px: 1,
                  "& .MuiAccordionSummary-content": { my: 0 },
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "11px" }}
                >
                  Include options
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 1,
                  py: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeAutoLoans}
                      onChange={(e) => {
                        setIncludeAutoLoans(e.target.checked);
                        console.debug(
                          "[Forecasting] Toggle debt type inclusion",
                          {
                            type: "auto",
                            included: e.target.checked,
                          }
                        );
                      }}
                    />
                  }
                  label="Include Auto Loans"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "11px" } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeMortgageLoans}
                      onChange={(e) => {
                        setIncludeMortgageLoans(e.target.checked);
                        console.debug(
                          "[Forecasting] Toggle debt type inclusion",
                          {
                            type: "mortgage",
                            included: e.target.checked,
                          }
                        );
                      }}
                    />
                  }
                  label="Include Mortgage"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "11px" } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeRvLoans}
                      onChange={(e) => {
                        setIncludeRvLoans(e.target.checked);
                        console.debug(
                          "[Forecasting] Toggle debt type inclusion",
                          {
                            type: "rv",
                            included: e.target.checked,
                          }
                        );
                      }}
                    />
                  }
                  label="Include RV Loans"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "11px" } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeBusinessLoans}
                      onChange={(e) => {
                        setIncludeBusinessLoans(e.target.checked);
                        console.debug(
                          "[Forecasting] Toggle debt type inclusion",
                          {
                            type: "business",
                            included: e.target.checked,
                          }
                        );
                      }}
                    />
                  }
                  label="Include Business Loans"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "11px" } }}
                />
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        {/* Total Paydown Target */}
        <Card
          sx={{
            borderTop: "4px solid #dd6b20",
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
            >
              Total Paydown
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: CARD_VALUE_FONT_SIZE }}
            >
              {displayCurrencyOrEmpty(totals.totalDebtPaydown)}
            </Typography>
          </CardContent>
        </Card>

        {/* Monthly Debt Payment */}
        <Tooltip
          title="Monthly Payment = sum of Max Monthly Payment across included debts. Click to view items."
          arrow
        >
          <Card
            sx={{
              borderTop: "4px solid #805ad5",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              cursor: hasSelectedProfile ? "pointer" : "default",
            }}
            onClick={() =>
              hasSelectedProfile
                ? setBreakdownModalType("monthlyPayment")
                : null
            }
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
              >
                Monthly Payment
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: CARD_VALUE_FONT_SIZE,
                  color:
                    hasSelectedProfile && totals.totalMonthlyDebtPayment < 0
                      ? "#e53e3e"
                      : "text.primary",
                }}
              >
                {displayCurrencyOrEmpty(totals.totalMonthlyDebtPayment)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Monthly Income */}
        <Tooltip
          title="Monthly Income = sum of invoice totals for current income records. Click to view items."
          arrow
        >
          <Card
            sx={{
              borderTop: "4px solid #38a169",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              cursor: hasSelectedProfile ? "pointer" : "default",
            }}
            onClick={() =>
              hasSelectedProfile ? setBreakdownModalType("monthlyIncome") : null
            }
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
              >
                Monthly Income
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: CARD_VALUE_FONT_SIZE,
                  color:
                    hasSelectedProfile && totals.totalMonthlyIncome < 0
                      ? "#e53e3e"
                      : "#38a169",
                }}
              >
                {displayCurrencyOrEmpty(totals.totalMonthlyIncome)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Monthly Expenses */}
        <Tooltip
          title="Monthly Expenses = current month expenses excluding skipped items. Click to view items."
          arrow
        >
          <Card
            sx={{
              borderTop: "4px solid #3182ce",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              cursor: hasSelectedProfile ? "pointer" : "default",
            }}
            onClick={() =>
              hasSelectedProfile
                ? setBreakdownModalType("monthlyExpenses")
                : null
            }
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
              >
                Monthly Expenses
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: CARD_VALUE_FONT_SIZE,
                  color: hasSelectedProfile ? "#e53e3e" : "text.primary",
                }}
              >
                {hasSelectedProfile
                  ? `-${formatCurrency(Math.abs(totals.totalMonthlyExpenses))}`
                  : "—"}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Net Monthly */}
        <Card
          sx={{
            borderTop: `4px solid ${totals.netMonthly >= 0 ? "#38a169" : "#e53e3e"}`,
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: CARD_TITLE_FONT_SIZE }}
            >
              Net Monthly
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: CARD_VALUE_FONT_SIZE,
                color: totals.netMonthly >= 0 ? "#38a169" : "#e53e3e",
              }}
            >
              {totals.netMonthly >= 0 ? "" : "-"}
              {hasSelectedProfile
                ? formatCurrency(Math.abs(totals.netMonthly))
                : "—"}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={addRateDialogOpen}
        onClose={() => setAddRateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add Hourly Rate</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            size="small"
            label="Hourly Rate"
            type="number"
            fullWidth
            inputProps={{ step: "0.01", min: 0.01 }}
            value={newHourlyRateInput}
            onChange={(e) => setNewHourlyRateInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddHourlyRate}
            disabled={
              !Number.isFinite(Number(newHourlyRateInput)) ||
              Number(newHourlyRateInput) <= 0
            }
          >
            Add Rate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={debtPickerOpen}
        onClose={handleCloseDebtPicker}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Total Debt Items ({activeDebts.length}/{debts.length})
        </DialogTitle>
        <DialogContent dividers>
          {debts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No debts available.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {debts.map((debt, index) => {
                const key = getDebtKey(debt, index);
                const included = !excludedDebtIdSet.has(key);
                const label =
                  [debt.creditor, debt.account].filter(Boolean).join(" • ") ||
                  "Debt";
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 0.5,
                      borderBottom: isDarkMode
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={included}
                          onChange={() => handleToggleDebt(debt, index)}
                        />
                      }
                      label={label}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatCurrency(Math.abs(Number(debt.balance || 0)))}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddDebtDialogOpen(true)}
          >
            Add Item
          </Button>
          <Button onClick={handleCloseDebtPicker}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDebtDialogOpen}
        onClose={() => setAddDebtDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Debt Item</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            <TextField
              size="small"
              label="Account"
              required
              value={newDebtForm.account}
              onChange={(e) =>
                setNewDebtForm((prev) => ({ ...prev, account: e.target.value }))
              }
            />
            <TextField
              size="small"
              label="Creditor"
              value={newDebtForm.creditor || ""}
              onChange={(e) =>
                setNewDebtForm((prev) => ({
                  ...prev,
                  creditor: e.target.value,
                }))
              }
            />
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <TextField
                size="small"
                label="Balance"
                type="number"
                inputProps={{ step: "0.01" }}
                value={newDebtForm.balance}
                onChange={(e) =>
                  setNewDebtForm((prev) => ({
                    ...prev,
                    balance: Number(e.target.value) || 0,
                  }))
                }
              />
              <TextField
                size="small"
                label="Max Monthly Payment"
                type="number"
                inputProps={{ step: "0.01" }}
                value={newDebtForm.maxMonthlyPayment}
                onChange={(e) =>
                  setNewDebtForm((prev) => ({
                    ...prev,
                    maxMonthlyPayment: Number(e.target.value) || 0,
                  }))
                }
              />
            </Box>
            <TextField
              size="small"
              label="Due Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newDebtForm.dueDate}
              onChange={(e) =>
                setNewDebtForm((prev) => ({ ...prev, dueDate: e.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDebtDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddDebtFromForecast}
            disabled={!newDebtForm.account.trim() || isAddingDebt}
          >
            {isAddingDebt ? "Adding..." : "Add Item"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(breakdownModalType)}
        onClose={() => setBreakdownModalType(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{breakdownTitle}</DialogTitle>
        <DialogContent dividers>
          {breakdownItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items available.
            </Typography>
          ) : (
            <List dense>
              {breakdownItems.map((item) => (
                <ListItem
                  key={item.id}
                  secondaryAction={
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatCurrency(Math.abs(item.amount || 0))}
                    </Typography>
                  }
                >
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreakdownModalType(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Forecast Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Profile Name"
            fullWidth
            variant="outlined"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newProfileName.trim()) {
                handleSaveProfile();
              }
            }}
            placeholder="e.g., Base Case, Aggressive, Conservative"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={!newProfileName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <PageShareModal
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        pageType="forecasting"
        payload={
          selectedProfileId
            ? {
                version: 1,
                profileName:
                  profiles.find((p) => p.id === selectedProfileId)?.name ||
                  "Shared Profile",
                settings: buildProfileSettings(),
              }
            : null
        }
        label={profiles.find((p) => p.id === selectedProfileId)?.name || null}
        buildShareUrl={(shareId) =>
          `${window.location.origin}/tools/debt-payoff-forecasting?shareId=${shareId}`
        }
      />

      <Dialog
        open={unlockDialogOpen}
        onClose={() => setUnlockDialogOpen(false)}
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={unlockPassword}
            onChange={(e) => setUnlockPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUnlockSharedProfile();
              }
            }}
          />
          {unlockError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {unlockError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUnlockSharedProfile}>
            Unlock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hours Per Day Input */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle2" sx={{ minWidth: 60 }}>
          Profiles
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Select Profile</InputLabel>
          <Select
            value={selectedProfileId}
            label="Select Profile"
            onChange={(e) => handleLoadProfile(e.target.value as string)}
          >
            <MenuItem value="">
              <em>New Profile</em>
            </MenuItem>
            {profiles.map((profile) => (
              <MenuItem key={profile.id} value={profile.id}>
                {profile.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Save Profile">
          <IconButton onClick={handleSaveOrUpdateProfile} size="small">
            <SaveIcon />
          </IconButton>
        </Tooltip>
        {selectedProfileId ? (
          <Tooltip title="Delete Profile">
            <IconButton
              onClick={() => handleDeleteProfile(selectedProfileId)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1">Days Per Week:</Typography>
          <TextField
            type="number"
            value={daysPerWeek}
            onChange={(e) =>
              setDaysPerWeek(Math.max(1, Math.min(7, Number(e.target.value))))
            }
            size="small"
            inputProps={{ min: 1, max: 7, step: 1 }}
            sx={{ width: 80 }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1">Working Hours:</Typography>
          <TextField
            type="number"
            value={hoursPerDay}
            onChange={(e) =>
              setHoursPerDay(Math.max(1, Math.min(24, Number(e.target.value))))
            }
            size="small"
            inputProps={{ min: 1, max: 24, step: 1 }}
            sx={{ width: 80 }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Deductions</InputLabel>
          <Select
            value={deductionMode}
            label="Deductions"
            onChange={(e) =>
              setDeductionMode(e.target.value as "percent" | "amount")
            }
          >
            <MenuItem value="percent">Percent of Gross</MenuItem>
            <MenuItem value="amount">Annual Amount</MenuItem>
          </Select>
        </FormControl>

        {deductionMode === "percent" ? (
          <TextField
            label="Deduction %"
            type="number"
            size="small"
            value={deductionPercent}
            onChange={(e) => {
              const next = Number(e.target.value);
              setDeductionPercent(
                Number.isFinite(next) ? Math.max(0, Math.min(100, next)) : 0
              );
            }}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={{ width: 140 }}
          />
        ) : (
          <TextField
            label="Annual Deductions"
            type="number"
            size="small"
            value={deductionAmountAnnual}
            onChange={(e) => {
              const next = Number(e.target.value);
              setDeductionAmountAnnual(
                Number.isFinite(next) ? Math.max(0, next) : 0
              );
            }}
            inputProps={{ min: 0, step: 100 }}
            sx={{ width: 180 }}
          />
        )}

        <Typography variant="body2" color="text.secondary">
          ({daysPerWeek} days/week assumed)
        </Typography>
      </Box>

      {/* Payoff Calculator Table */}
      <Box
        sx={{
          mt: 4,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Time to Pay Off{" "}
          {hasSelectedProfile ? formatCurrency(totals.totalDebtBalance) : "—"}{" "}
          in Debt
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddRateDialogOpen(true)}
        >
          Add Hourly Rate
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: isDarkMode ? "#1a1a1a" : "white",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
              }}
            >
              <TableCell sx={{ fontWeight: 600 }}>Hourly Rate</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Daily ({hoursPerDay}hrs)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Weekly
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Monthly
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Yearly Gross
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Taxes
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Annual Expenses
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Yearly Net
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  backgroundColor: isDarkMode ? "#252525" : "#f0f4f8",
                }}
              >
                Time to Payoff
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payoffCalculations.map((calc, index) => (
              <TableRow
                key={calc.rate}
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: isDarkMode
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.02)",
                  },
                }}
              >
                <TableCell sx={{ fontWeight: 600 }}>${calc.rate}/hr</TableCell>
                <TableCell align="right">
                  {hasSelectedProfile
                    ? formatCurrency(calc.dailyEarnings)
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile
                    ? formatCurrency(calc.weeklyEarnings)
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile
                    ? formatCurrency(calc.monthlyEarnings)
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile
                    ? formatCurrency(calc.yearlyEarnings)
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile ? formatCurrency(calc.yearlyTaxes) : "—"}{" "}
                  {hasSelectedProfile && calc.yearlyEarnings > 0
                    ? `(${formatPercent(calc.yearlyTaxes / calc.yearlyEarnings)})`
                    : hasSelectedProfile
                      ? "(0%)"
                      : ""}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile ? formatCurrency(annualExpenses) : "—"}
                </TableCell>
                <TableCell align="right">
                  {hasSelectedProfile ? formatCurrency(calc.yearlyNet) : "—"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: isDarkMode ? "#252525" : "#f0f4f8",
                    color: !hasSelectedProfile
                      ? "text.primary"
                      : calc.yearsToPayoff < 1
                        ? "#38a169"
                        : calc.yearsToPayoff < 3
                          ? "#dd6b20"
                          : "#e53e3e",
                  }}
                >
                  {hasSelectedProfile
                    ? formatTimeDisplay(
                        calc.daysToPayoff,
                        calc.weeksToPayoff,
                        calc.monthsToPayoff,
                        calc.yearsToPayoff
                      )
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Additional Info */}
      <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Tooltip title="Time to payoff uses net earnings (after taxes and expenses).">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Annual expenses use current total monthly expenses × 12.">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Deductions reduce taxable income; includes annual expenses.">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Taxes assume 2025 single-filer W-2 income in Oregon with the standard deduction.">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={`Work schedule: ${hoursPerDay} hours/day, ${daysPerWeek} days/week, ~${Math.round(
            annualWorkDays
          )} work days/year.`}
        >
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Forecasting;
